import { formatTime } from "@circle-react/components/shared/uikit/TipTap/VoiceMessage/helpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Modal } from "@circleco/compass/components/Modal";
import { noop } from "lodash";

import { t } from "@/i18n-js/instance";
import type { CompassModalProps } from "@/react/components/SettingsApp/CompassCompat/modalHelpers";
import { legacyClassNames } from "@/react/helpers/cssAnchorHelpers";

import type { VoiceMessageModalProps } from "./types";
import type { VoiceMessageRecordingProps } from "./useVoiceMessageRecording";
import { useVoiceMessageRecording } from "./useVoiceMessageRecording";

// Rendered inside Modal.Content so the waveform container is in the DOM by
// the time useVoiceMessageRecording initializes wavesurfer (the modal content
// mounts in a later commit than the component that owns Modal.Root).
const RecorderContent = ({
  editor,
  range,
  addToLocalInlineAttachments,
  onClose,
}: VoiceMessageRecordingProps) => {
  const { duration, handleClose, handleSubmit, isBusy, waveformRef } =
    useVoiceMessageRecording({
      addToLocalInlineAttachments,
      editor,
      onClose,
      range,
    });

  return (
    <>
      <Modal.Body>
        <div className="border-primary flex items-center gap-3 rounded-xl border p-2">
          <div
            ref={waveformRef}
            className={classNames(
              legacyClassNames("audio-player-wavesurfer__waveform"),
              "min-w-0 flex-1",
            )}
          />
          <span className="text-default shrink-0 whitespace-nowrap pr-1 text-sm tabular-nums">
            {formatTime(duration)}
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer
        secondaryAction={{
          label: t("cancel"),
          onClick: handleClose,
          shouldCloseModal: false,
        }}
        primaryAction={{
          label: t("continue"),
          onClick: handleSubmit,
          loading: isBusy,
        }}
      />
    </>
  );
};

export const VoiceMessageModalComponent = ({
  editor,
  range,
  addToLocalInlineAttachments = noop,
  open,
  onOpenChange,
}: VoiceMessageModalProps & CompassModalProps) => (
  <Modal.Root open={open} onOpenChange={onOpenChange} size="md">
    <Modal.Content>
      <Modal.Header title={t("tiptap.voice_message.action")} />
      <RecorderContent
        editor={editor}
        range={range}
        addToLocalInlineAttachments={addToLocalInlineAttachments}
        onClose={() => onOpenChange(false)}
      />
    </Modal.Content>
  </Modal.Root>
);
